import type { ReactNode } from 'react';
import { Offcanvas, OffcanvasHeader, OffcanvasBody } from 'reactstrap';

interface DrawerProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  /**
   * Optional width override (es. "640px", "70%"). Quando passato vince
   * sul comportamento responsive di default — utile per drawer che
   * richiedono una larghezza fissa (raro).
   */
  width?: string;
  /**
   * SP-8g — drawer NON modale: con `backdrop={false}` sparisce il velo grigio
   * → la pagina sotto resta visibile e usabile, e il click fuori NON chiude
   * più il drawer (resta solo `×`/Escape). Default `true` (comportamento
   * modale invariato per i 10+ drawer esistenti). Opt-in: usato dal wizard
   * di split per non oscurare la lista sotto.
   */
  backdrop?: boolean;
  /**
   * SP-8g — consente lo scroll del `<body>` mentre il drawer è aperto (non
   * blocca la pagina sotto). Coerente con `backdrop={false}`. Default `false`.
   */
  scroll?: boolean;
}

/**
 * Side drawer. Public API invariata; migrato a reactstrap `<Offcanvas
 * direction="end">` (Velzon). Escape + backdrop click chiudono il drawer
 * (default reactstrap).
 *
 * SP-7c — comportamento responsive: senza `width` prop esplicito, applica
 * la classe `vp-drawer-responsive` che (vedi vp-custom.scss):
 *   - 100% su xs (< 576px)
 *   - 80%  su sm-md (576-991px)
 *   - 50%  su lg+ (>= 992px)
 *
 * Usato in 10+ pagine — i consumer NON cambiano API.
 */
export function Drawer({
  isOpen, onClose, title, children, width, backdrop = true, scroll = false,
}: DrawerProps) {
  // Quando `width` è passato esplicitamente, l'utente del componente
  // vuole un override fisso → usa style inline e NON la classe responsive.
  const useResponsiveClass = width === undefined;
  return (
    <Offcanvas
      isOpen={isOpen}
      toggle={onClose}
      direction="end"
      backdrop={backdrop}
      scroll={scroll}
      className={useResponsiveClass ? 'vp-drawer-responsive' : undefined}
      style={useResponsiveClass ? undefined : { width }}
    >
      <OffcanvasHeader toggle={onClose}>{title}</OffcanvasHeader>
      <OffcanvasBody>{children}</OffcanvasBody>
    </Offcanvas>
  );
}
